const sensitiveKeys =
  /^(token|(?:invite|magic|access|id|refresh)_?token|code|state|authorization|x-magic-token)$/i;

function redactUrl(value: string) {
  return value.replace(
    /(^|[?&#])([^=?&#\s]+)=([^&#\s]*)/g,
    (match, separator: string, name: string) => {
      try {
        return sensitiveKeys.test(decodeURIComponent(name.replace(/\+/g, ' ')))
          ? `${separator}${name}=[redacted]`
          : match;
      } catch {
        return match;
      }
    }
  );
}

export function redactAuthTelemetry<T>(value: T): T {
  return sanitize(value, new WeakSet());
}

function sanitize<T>(value: T, ancestors: WeakSet<object>): T {
  if (typeof value === 'string') {
    if (value.trimStart().startsWith('{') || value.trimStart().startsWith('[')) {
      try {
        return JSON.stringify(sanitize(JSON.parse(value), ancestors)) as T;
      } catch {
        // Non-JSON strings still need URL/query-string sanitization.
      }
    }
    return redactUrl(value) as T;
  }
  if (value !== null && typeof value === 'object') {
    if (ancestors.has(value)) return '[Circular]' as T;
    ancestors.add(value);
    try {
      if (Array.isArray(value)) return value.map((item) => sanitize(item, ancestors)) as T;
      const properties =
        value instanceof Error
          ? {
              ...value,
              name: value.name,
              message: value.message,
              stack: value.stack,
              ...('cause' in value ? { cause: value.cause } : {}),
            }
          : value;
      return Object.fromEntries(
        Object.entries(properties).map(([key, item]) => [
          key,
          sensitiveKeys.test(key) ? '[redacted]' : sanitize(item, ancestors),
        ])
      ) as T;
    } finally {
      ancestors.delete(value);
    }
  }
  return value;
}

/** Mixpanel's public project token is routing metadata, not an authentication credential. */
export function redactMixpanelEvent<T extends { properties: Record<string, unknown> }>(
  event: T
): T {
  const sanitized = redactAuthTelemetry(event);
  if ('token' in event.properties) sanitized.properties.token = event.properties.token;
  return sanitized;
}
